import React, { useState, useRef, FC, ReactElement } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { doc, collection, getDocs, deleteDoc, updateDoc } from 'firebase/firestore';
import { AgeCategory } from '../../types/camper';
import { createPrimaryCamper, updatePrimaryCamper } from '../../utils/camperManagement';
import './DevelopmentTools.css';

import DevelopmentNotes from './DevelopmentNotes';
import CamperAuthLinker from './DevTools/CamperAuthLinker';
import AccommodationHistoryMigration from './DevTools/AccommodationHistoryMigration';
import PhotoAudit from './DevTools/PhotoAudit';
import FacilitiesEntry from './DevTools/FacilitiesEntry';

import { getAuth } from 'firebase/auth';

// Add interfaces for CSV processing
interface CamperGroup {
  primaryCamper: any;
  dependants: {
    FirstName: string;
    LastName: string;
    Age: AgeCategory;
  }[];
}

// Add this helper function before the DevelopmentTools component
const confirmAction = (message: string): boolean => {
  // eslint-disable-next-line no-restricted-globals
  return window.confirm(message);
};



const DevelopmentTools: FC = (): ReactElement => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [searchFirstName, setSearchFirstName] = useState('');
  const [searchLastName, setSearchLastName] = useState('');
  const [selectedAuthCamper, setSelectedAuthCamper] = useState<{
    id: string;
    name: string;
    email: string;
    authUid?: string;
  } | null>(null);
  const [camperList, setCamperList] = useState<React.ReactNode | null>(null);

  





  const processCSVRow = async (headers: string[], row: string[], index: number) => {
    const warnings: string[] = [];
    
    // Pad the row with empty strings if it has fewer columns than expected
    const paddedRow = [...row];
    while (paddedRow.length < headers.length) {
      paddedRow.push('');
    }

    // Now process the padded row
    const camper: any = {};
    const requiredFields = ['FirstName', 'LastName'];

    // Validate required fields
    for (const field of requiredFields) {
      const value = paddedRow[headers.indexOf(field)]?.trim();
      if (!value) {
        throw new Error(`Row ${index + 1}: Required field "${field}" is missing`);
      }
    }

    // Process and validate each field
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i];
      let value = paddedRow[i];

      try {
        // Apply appropriate cleaning based on field type
        if (header === 'HomePhone' || header === 'MobilePhone') {
          value = value.replace(/\s*Cell\s*/i, '').trim();
        } else if (header === 'PostalCode') {
          value = value.replace(/\s+/g, '').trim();
        } else if (header.startsWith('Acc')) {
          value = value.replace(/[\n\r]+/g, '').trim().toUpperCase();
        } else if (header === 'email') {
          value = value.trim().toLowerCase();
          if (!value.includes('@') || !value.includes('.') || 
              value.includes('snail mail') || value.includes('no email') || value.includes('bounced')) {
            value = '';
          }
        } else {
          value = value.trim();
        }

        // Log warnings for potentially problematic data
        if (value && header === 'email' && !value.includes('@')) {
          warnings.push(`Row ${index + 1}: Invalid email format`);
        }
        if (value && header === 'PostalCode' && value.length < 6) {
          warnings.push(`Row ${index + 1}: Suspicious postal code length`);
        }

        if (value) {
          camper[header] = value;
        }
      } catch (err: any) {
        warnings.push(`Row ${index + 1}: Error processing ${header}: ${err.message}`);
      }
    }

    // Create the camper record
    const newCamper = await createPrimaryCamper(camper);

    return { camper: newCamper, warnings };
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError('');
    setSuccess('');

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const rows = text.split('\n').map(row => 
          row.split(',').map(cell => 
            cell.trim().replace(/^"|"$/g, '') // Remove quotes and trim
          )
        );

        const headers = rows[0];
        const dataRows = rows.slice(1).filter(row => row.some(cell => cell.trim()));

        console.log(`Processing ${dataRows.length} rows...`);
        console.log('Headers:', headers);
        
        // Get indices for all required fields
        const idIndex = headers.indexOf('ID');
        const firstNameIndex = headers.indexOf('FirstName');
        const lastNameIndex = headers.indexOf('LastName');
        const peopleFirstNameIndex = headers.indexOf('People::First Name');
        const peopleLastNameIndex = headers.indexOf('People::Last Name');

        // Validate required fields
        if (idIndex === -1) {
          throw new Error('Required field "ID" is missing from CSV');
        }
        if (firstNameIndex === -1 || lastNameIndex === -1) {
          throw new Error('Required fields "FirstName" and/or "LastName" are missing from CSV');
        }

        // First, group all rows by ID
        const rowsByID = new Map<string, string[][]>();
        for (const row of dataRows) {
          const id = row[idIndex]?.trim();
          if (!id) continue;

          if (!rowsByID.has(id)) {
            rowsByID.set(id, []);
          }
          rowsByID.get(id)?.push(row);
        }

        // Now process each group of rows
        const camperGroups = new Map<string, CamperGroup>();
        let successCount = 0;
        let skippedRows: string[] = [];

        for (const [id, rows] of Array.from(rowsByID.entries())) {
          try {
            // First row should contain primary camper info
            const primaryRow = rows[0];
            const firstName = primaryRow[firstNameIndex]?.trim() || '';
            const lastName = primaryRow[lastNameIndex]?.trim() || '';

            if (!firstName || !lastName) {
              skippedRows.push(`ID ${id}: Missing required fields - FirstName: "${firstName}", LastName: "${lastName}"`);
              continue;
            }

            // Process primary camper
            const { camper, warnings } = await processCSVRow(headers, primaryRow, dataRows.indexOf(primaryRow) + 1);
            if (warnings.length > 0) {
              skippedRows.push(...warnings);
            }

            // Initialize camper group
            camperGroups.set(id, {
              primaryCamper: camper,
              dependants: []
            });

            // Process all rows for dependants
            const group = camperGroups.get(id);
            if (group && peopleFirstNameIndex !== -1 && peopleLastNameIndex !== -1) {
              for (const row of rows) {
                const depFirstName = row[peopleFirstNameIndex]?.trim();
                const depLastName = row[peopleLastNameIndex]?.trim();

                if (depFirstName && depLastName) {
                  // Check if this is the primary camper
                  const isPrimaryCamper = 
                    depFirstName.toLowerCase() === firstName.toLowerCase() && 
                    depLastName.toLowerCase() === lastName.toLowerCase();

                                     // Check for duplicates
                   const isDuplicate = group.dependants.some((dep: { FirstName: string; LastName: string }) => 
                     dep.FirstName.toLowerCase() === depFirstName.toLowerCase() && 
                     dep.LastName.toLowerCase() === depLastName.toLowerCase()
                   );

                  if (!isPrimaryCamper && !isDuplicate) {
                    group.dependants.push({
                      FirstName: depFirstName,
                      LastName: depLastName,
                      Age: AgeCategory.MustChange
                    });
                    console.log(`Added dependant ${depFirstName} ${depLastName} to camper ${id}`);
                  } else {
                    console.log(
                      isPrimaryCamper 
                        ? `Skipped primary camper ${depFirstName} ${depLastName} as dependant for ${id}`
                        : `Skipped duplicate dependant ${depFirstName} ${depLastName} for ${id}`
                    );
                  }
                }
              }
            }

            successCount++;
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            console.error(`Error processing ID ${id}:`, errorMessage);
            skippedRows.push(`ID ${id}: ${errorMessage}`);
          }
        }

        // Update all camper records with their dependants
        for (const [id, group] of Array.from(camperGroups.entries())) {
          try {
                         console.log(`Processing camper ${id} with ${group.dependants.length} dependants:`, 
               group.dependants.map((d: { FirstName: string; LastName: string }) => `${d.FirstName} ${d.LastName}`).join(', '));
            
            await updatePrimaryCamper(group.primaryCamper.recordId, {
              dependants: group.dependants
            });
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            console.error(`Error updating camper ${id}:`, errorMessage);
            skippedRows.push(`Camper ${id}: ${errorMessage}`);
          }
        }

        // Set success message with information about skipped rows
        let message = `Successfully imported ${successCount} campers.`;
        if (skippedRows.length > 0) {
          message += `\n\nWarnings and Errors:\n${skippedRows.join('\n')}`;
        }
        setSuccess(message);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    reader.readAsText(file);
  };



  const handleLinkAccount = async (camperId: string, authUid: string) => {
    try {
      const camperRef = doc(db, 'primaryCampers', camperId);
      await updateDoc(camperRef, {
        authUid,
        loginExists: true
      });
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Failed to link account');
    }
  };





  if (!isAdmin) {
    return (
      <div className="dev-tools">
        <h1>Access Denied</h1>
        <p>You need admin privileges to access development tools.</p>
      </div>
    );
  }

  return (
    <div className="development-tools">
      <h1>Development Tools</h1>

      {/* Photo Audit Tool */}
      <PhotoAudit />

      {/* Facilities Data Entry */}
      <FacilitiesEntry />

      {/* Migration Tools */}
      <AccommodationHistoryMigration />
      
      {/* Auth Account Linker Section */}
      <section>
        <div className="action-section">
          <div className="action-header">
            <h3>Auth Account Linker</h3>
            <p>Link Firebase Authentication accounts to Primary Campers.</p>
          </div>
          
          {!selectedAuthCamper && (
            <div className="search-section">
              <div className="search-inputs">
                <input
                  type="text"
                  value={searchFirstName}
                  onChange={(e) => setSearchFirstName(e.target.value)}
                  placeholder="First Name"
                />
                <input
                  type="text"
                  value={searchLastName}
                  onChange={(e) => setSearchLastName(e.target.value)}
                  placeholder="Last Name"
                />
                <button
                  onClick={async () => {
                    if (!searchFirstName && !searchLastName) {
                      setError('Please enter at least a first or last name');
                      return;
                    }
                    setLoading(true);
                    setError('');
                    setCamperList(null); // Clear previous results
                    try {
                      const firstNameLower = searchFirstName.toLowerCase();
                      const lastNameLower = searchLastName.toLowerCase();

                      const campersRef = collection(db, 'primaryCampers');
                      const snapshot = await getDocs(campersRef);
                      
                      const matchingCampers = snapshot.docs.filter(doc => {
                        const data = doc.data();
                        const firstName = (data.FirstName || '').toLowerCase();
                        const lastName = (data.LastName || '').toLowerCase();

                        if (searchFirstName && searchLastName) {
                          return firstName.startsWith(firstNameLower) && lastName.startsWith(lastNameLower);
                        } else if (searchFirstName) {
                          return firstName.startsWith(firstNameLower);
                        } else {
                          return lastName.startsWith(lastNameLower);
                        }
                      });

                      if (matchingCampers.length === 0) {
                        setError('No camper found with these names');
                        return;
                      }

                      // Create a list of buttons for each camper
                      const campers = matchingCampers.map(doc => {
                        const data = doc.data();
                        return {
                          id: doc.id,
                          name: `${data.FirstName} ${data.LastName}`,
                          email: data.email,
                          authUid: data.authUid
                        };
                      });

                      // Sort results alphabetically by name
                      campers.sort((a, b) => a.name.localeCompare(b.name));

                      const camperListElement = (
                        <div className="camper-list">
                          {campers.map(camper => (
                            <button
                              key={camper.id}
                              className="action-button"
                              onClick={() => {
                                setSelectedAuthCamper(camper);
                                setSearchFirstName('');
                                setSearchLastName('');
                                setCamperList(null);
                              }}
                            >
                              {camper.name}{camper.email ? ` (${camper.email})` : ''}
                              {camper.authUid ? ' [Linked]' : ''}
                            </button>
                          ))}
                        </div>
                      );

                      setSuccess(`Found ${campers.length} campers. Please select one:`);
                      setCamperList(camperListElement);
                    } catch (err) {
                      console.error('Error finding camper:', err);
                      setError('Failed to find camper');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading || (!searchFirstName && !searchLastName)}
                  className="action-button"
                >
                  Find Camper
                </button>
              </div>
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              {camperList}
            </div>
          )}

          {selectedAuthCamper && (
            <CamperAuthLinker
              selectedCamper={selectedAuthCamper}
              onClose={() => {
                setSelectedAuthCamper(null);
                setSuccess('');
              }}
              auth={getAuth()}
              onLinkAccount={handleLinkAccount}
            />
          )}
        </div>
      </section>

      {/* Development Notes Section */}
      <section>
        <DevelopmentNotes />
      </section>

      {/* Danger Zone Section */}
      <section>
        <h2>Danger Zone</h2>
        <p className="warning-text">⚠️ These actions can cause permanent data loss. Use with caution!</p>

        <div className="danger-action">
          <div className="danger-header">
            <h3>Import Camper Data</h3>
            <p>Import camper data from a CSV file. This operation will create new camper records in the system.</p>
          </div>
          <div className="upload-section">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              ref={fileInputRef}
              disabled={loading}
            />
            {loading && <div className="loading">Processing...</div>}
            {success && <div className="success-message">{success}</div>}
          </div>
        </div>

        <div className="danger-action">
          <div className="danger-header">
            <h3>Clear Email Queue</h3>
            <p>Delete all pending emails from the emails collection. This will prevent any queued emails from being sent.</p>
          </div>
          <button
            onClick={async () => {
              if (!confirmAction('Are you sure you want to delete ALL emails from the queue? This cannot be undone.')) {
                return;
              }
              setLoading(true);
              setError('');
              try {
                const emailsRef = collection(db, 'emails');
                const snapshot = await getDocs(emailsRef);
                const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
                await Promise.all(deletePromises);
                setSuccess(`Successfully deleted ${snapshot.size} email(s) from the queue`);
              } catch (err) {
                console.error('Error clearing email queue:', err);
                setError('Failed to clear email queue');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="danger-button"
          >
            Clear Email Queue
          </button>
        </div>

      </section>

    </div>
  );
};

export default DevelopmentTools; 